<script lang="ts">
  /**
   * P5: sales-pipeline board (HubSpot/Pipedrive style). Stage columns with color,
   * count and conversion stats; click a stage to load the entities currently in it.
   * Reads from funnel_entity_state (current occupancy); colors via columnAccent.
   */
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import { columnAccent } from '../lib/kanban-colors';
  import { fmtInt, fmtPct, fmtUah, fmtRelative } from '../lib/format';
  import {
    fetchFunnelPipeline,
    fetchStageEntities,
    type FunnelPipeline,
    type StageEntity,
  } from '../lib/funnels-api';

  let { slug }: { slug: string } = $props();

  let pipeline = $state<FunnelPipeline | null>(null);
  let loading = $state(true);
  let err = $state<string | null>(null);

  // Lazy-loaded entity lists per stage.
  let entitiesByStage = $state<Record<string, { items: StageEntity[]; total: number; loading: boolean }>>({});

  onMount(async () => {
    try {
      pipeline = await fetchFunnelPipeline(slug);
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  });

  async function loadStage(stageId: string) {
    if (entitiesByStage[stageId]?.items?.length) return; // already loaded
    entitiesByStage[stageId] = { items: [], total: 0, loading: true };
    entitiesByStage = entitiesByStage;
    try {
      const r = await fetchStageEntities(slug, stageId, 0, 30);
      entitiesByStage[stageId] = { items: r.items, total: r.total, loading: false };
    } catch {
      entitiesByStage[stageId] = { items: [], total: 0, loading: false };
    }
    entitiesByStage = entitiesByStage;
  }

  function fmtAvg(sec: number | null): string {
    if (sec == null) return '—';
    if (sec < 3600) return `${Math.round(sec / 60)}хв`;
    if (sec < 86400) return `${Math.round(sec / 3600)}год`;
    return `${Math.round(sec / 86400)}д`;
  }
</script>

{#if loading}
  <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
{:else if err}
  <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
{:else if pipeline}
  <div class="mb-3 flex items-center gap-2 text-[11px] text-[var(--color-text-muted)]">
    <Icon name="funnels" size={14} />
    <span><strong class="text-[var(--color-text)]">{fmtInt(pipeline.totalEntities)}</strong> сутностей у воронці</span>
  </div>
  <div class="flex gap-3 overflow-x-auto pb-3">
    {#each pipeline.stages as st (st.id)}
      {@const accent = columnAccent(st.slug)}
      <div class="flex-shrink-0 w-[260px] rounded-xl border bg-white" style="border-color: {accent.border};">
        <!-- header -->
        <div class="rounded-t-xl px-3 py-2.5" style="background: {accent.bg};">
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="h-2.5 w-2.5 rounded-full shrink-0" style="background: {accent.dot};"></span>
              <span class="text-[12.5px] font-bold truncate" style="color: {accent.text};">{st.name}</span>
            </div>
            <span class="text-[13px] font-bold tabular-nums" style="color: {accent.text};">{fmtInt(st.count)}</span>
          </div>
          <div class="mt-1.5 grid grid-cols-3 gap-1 text-[9.5px]">
            <div class="rounded bg-white/60 px-1 py-0.5 text-center">
              <div class="font-bold tabular-nums" style="color: {accent.text};">{fmtPct(st.conversionPct)}</div>
              <div class="text-[var(--color-text-muted-2)]">конв.</div>
            </div>
            <div class="rounded bg-white/60 px-1 py-0.5 text-center">
              <div class="font-bold tabular-nums" style="color: {accent.text};">{fmtAvg(st.avgTimeSec)}</div>
              <div class="text-[var(--color-text-muted-2)]">час</div>
            </div>
            <div class="rounded bg-white/60 px-1 py-0.5 text-center">
              <div class="font-bold tabular-nums" style="color: {accent.text};">{st.revenueCents > 0 ? fmtUah(st.revenueCents, { compact: true }) : '—'}</div>
              <div class="text-[var(--color-text-muted-2)]">дохід</div>
            </div>
          </div>
          {#if st.ordinal > 0}
            <div class="mt-1 text-[9px] text-[var(--color-text-muted-2)]">
              з попереднього: <span class="font-semibold tabular-nums">{fmtPct(st.pctOfPrev)}</span>
              · відтік: <span class="font-semibold tabular-nums">{fmtPct(st.dropoffPct)}</span>
            </div>
          {/if}
        </div>

        <!-- entities -->
        <div class="p-2">
          {#if !entitiesByStage[st.id]}
            <button
              onclick={() => loadStage(st.id)}
              class="w-full rounded-md border border-dashed border-[var(--color-border-3)] py-2 text-[10.5px] font-semibold text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
              disabled={st.count === 0}
            >
              {st.count === 0 ? 'Порожньо' : `Показати ${fmtInt(st.count)}`}
            </button>
          {:else if entitiesByStage[st.id].loading}
            <div class="py-3 text-center text-[10.5px] text-[var(--color-text-muted)]">Завантаження…</div>
          {:else}
            <div class="flex flex-col gap-1.5">
              {#each entitiesByStage[st.id].items as e (e.entityId)}
                <a
                  href={e.href}
                  class="block rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1.5 no-underline hover:border-[var(--color-pu-3)] hover:shadow-sm"
                >
                  <div class="text-[11.5px] font-semibold text-[var(--color-text)] truncate">{e.label}</div>
                  <div class="flex items-center justify-between gap-2">
                    {#if e.sub}<div class="text-[10px] text-[var(--color-text-muted)] truncate">{e.sub}</div>{/if}
                    {#if e.lastReachedAt}<div class="ml-auto text-[9.5px] text-[var(--color-text-muted-2)] whitespace-nowrap">{fmtRelative(e.lastReachedAt)}</div>{/if}
                  </div>
                </a>
              {/each}
              {#if entitiesByStage[st.id].total > entitiesByStage[st.id].items.length}
                <div class="text-center text-[9.5px] text-[var(--color-text-muted-2)]">
                  +{fmtInt(entitiesByStage[st.id].total - entitiesByStage[st.id].items.length)} ще
                </div>
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {/each}
  </div>
{/if}
